import type { ReactElement } from 'react';

import { BREAKPOINTS } from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import {
  BpkFlex,
  BpkSpacing,
  BpkVessel,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import { useEverywhereSearchNavigation } from '@web-platform/shared-banana-redux/src/helpers/navigationHandlers/useEverywhereSearchNavigation';
import { useBehaviourTracking } from '@web-platform/shared-banana-tracking/src/trackMBD';
import { useMediaQuery } from '@web-platform/shared-banana-utils-frontend/src/helpers/hooks/useMediaQuery/useMediaQuery';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { MobileScrollContainer } from '@web-platform/shared-universal-ui-mobile-scroll-container';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import { isExploreAIAvailable } from '@web-platform/shared-universal-utils-frontend/src/helpers/isExploreAIAvailable';
import { logOperationalEvent } from 'saddlebag-logger';

import { GlobalNavigationFallbackCard } from './GlobalNavigationFallbackCard/GlobalNavigationFallbackCard';
import { NAV_CARD_TYPES, getGlobalNavCards } from './hardcodedNavigationFallbackCards';

import STYLES from './GlobalNavigationFallback.module.scss';

export type OwnProps = {
  parentPage: string;
};

export type Props = OwnProps;

export const GlobalNavigationFallback = ({ parentPage }: Props): ReactElement => {
  const shouldShowExploreAICard = isExploreAIAvailable();
  const isStaysNavcardEnabled = getFeatureDecision(FEATURE_DECISION_KEYS.ENABLE_STAYS_NAVCARD);
  const cards = getGlobalNavCards(shouldShowExploreAICard, isStaysNavcardEnabled);

  const { ref: hotelRef } = useBehaviourTracking({
    componentName: 'global_navigation_fallback_card',
    vertical: 'hotel',
  });

  const { ref: carHireRef } = useBehaviourTracking({
    componentName: 'global_navigation_fallback_card',
    vertical: 'car_hire',
  });

  const getCardRef = (type: string) => {
    if (type === NAV_CARD_TYPES.HOTELS) {
      return hotelRef;
    }
    if (type === NAV_CARD_TYPES.CAR_HIRE) {
      return carHireRef;
    }

    return null;
  };

  const { handleEverywhereSearchNavigation } = useEverywhereSearchNavigation({
    tracking: { trackInteractionEvent },
  });

  const onGlobalNavCardClicked = async (
    id: number,
    title: string,
    type: string,
    CTAUrl: string,
    label: string,
  ): Promise<void> => {
    const payload = {
      strings: {
        title,
        pageName: parentPage,
        type,
        CTAUrl,
        label,
      },
      numbers: {
        index: id,
      },
    };
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.GLOBAL_NAV_FALLBACK_CARD_CLICKED.NAME,
      ...payload,
    });

    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.GLOBAL_NAV_FALLBACK_CARD_CLICKED.NAME,
      ...payload.strings,
      ...payload.numbers,
    });

    if (type === NAV_CARD_TYPES.EXPLORE_EVERYWHERE) {
      handleEverywhereSearchNavigation();
    }
  };

  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE);
  const renderGlobalNavigationFallbackCardsContainer = () => (
    <BpkVessel className={STYLES.cardsContainer}>
      <BpkFlex columnGap={{ base: BpkSpacing.MD, desktop: BpkSpacing.Base }}>
        {cards.map((card) => (
          <GlobalNavigationFallbackCard
            index={card.id}
            key={card.id}
            CTAUrl={card.link}
            pageName={parentPage}
            title={card.title}
            type={card.type}
            label={card.label}
            ref={getCardRef(card.type)}
            onClick={() =>
              onGlobalNavCardClicked(card.id, card.title, card.type, card.link, card.label)
            }
          />
        ))}
      </BpkFlex>
    </BpkVessel>
  );

  return (
    <BpkVessel className={STYLES.container}>
      {isMobile ? (
        <MobileScrollContainer>
          {renderGlobalNavigationFallbackCardsContainer()}
        </MobileScrollContainer>
      ) : (
        renderGlobalNavigationFallbackCardsContainer()
      )}
    </BpkVessel>
  );
};
